// Dugout "Elite League" design system tokens.
//
// IMPORTANT: src/index.css defines a `.light` class that inverts the Tailwind zinc
// palette, so white surfaces must never be styled with Tailwind's NAMED color
// utilities (bg-white, text-zinc-*). Use the semantic token utilities instead —
// bg-surface / bg-canvas / text-ink / text-ink-2 / border-hairline — which are
// declared in the `@theme` block at the top of src/index.css.
//
// These objects MIRROR that @theme block one-for-one (GS.ink === --color-ink, and
// so on). They are deliberately kept as literal hex rather than `var(--color-*)`:
// 13 call sites pass them straight into SVG *presentation attributes*
// (`stroke={GS.line}`, `fill={GS.ink3}`, `stopColor={GS.green}` in LandingPage,
// StoryTiles and stats/ui) and browsers do NOT resolve var() in SVG attributes —
// repointing these at CSS variables blanks those graphics with no error. If you
// change a value here, change it in the @theme block too.
//
// Preference order for new code: token utility class > GS.* inline style > hex.

import type React from 'react';

// Phase 2: DS repointed to the Green & Slate ("Under the Lights") palette so all
// DS-consuming screens shift at once. Names kept for compatibility; gold tokens
// stay gold (Elite-only). `green` historically rendered near-black (#0D0D0D) and
// is kept as ink so existing usages don't unexpectedly turn bright green.
export const DS = {
  bg:        '#FFFFFF',  // card/panel surface (GS.panel)
  bgAlt:     '#F7F8F9',  // slate canvas (GS.bg)
  sep:       '#E3E7EB',  // slate hairline (GS.line)
  textPri:   '#141A21',  // 100% — near-black slate ink, primary text (GS.ink)
  textSec:   '#5A6672',  // 75% — readable secondary slate (GS.ink2). 5.52:1 on canvas.
                         // THE default for all secondary/muted/label TEXT.
  // ⚠ The four below FAIL WCAG AA as text (3.0:1, 2.4:1, 1.6:1, 1.9:1 on canvas).
  // Non-text only: disabled controls, dividers, icon strokes, decorative rules.
  // Any muted COPY — including micro-labels like AVG/OPS/ERA — uses textSec.
  textDisabled: '#8B919A', // non-text: disabled control chrome
  textMuted: '#9AA5B1',  // non-text: dividers, icon strokes
  textDim:   '#C2CBD4',  // non-text: faint rules
  dim:       '#AEB8C2',  // non-text
  gold:      '#C9A84C',  // Elite/premium ONLY — fills/marks, NOT text (2.15:1)
  goldBg:    '#F5EDD6',
  goldText:  '#8C6D1E',  // gold TEXT — deepened from #B8912E, which was 2.77:1
  green:     '#141A21',  // legacy: rendered as ink — keep as ink, not accent
  red:       '#E5484D',  // fill only (3.68:1). Error TEXT uses #C62828.
};

// ── Green & Slate ("Under the Lights") palette ──────────────────────────────
// New design direction: near-white slate canvas, white cards, green as the
// everyday accent, gold reserved EXCLUSIVELY for Elite/premium markers.
// Introduced alongside DS during the phased rollout — landing page first, then
// home, then the rest of the app repoints DS.* at these values (Phase 2).
export const GS = {
  bg:        '#F7F8F9',  // near-white slate-tinted canvas (not pure white)
  panel:     '#FFFFFF',  // card surface
  panel2:    '#F1F3F5',  // raised tile / inset
  line:      '#E3E7EB',  // slate hairline
  ink:       '#141A21',  // near-black slate text — 16.5:1 on canvas
  ink2:      '#5A6672',  // secondary slate text — 5.52:1, the default for muted TEXT
  ink3:      '#9AA5B1',  // ⚠ 2.35:1 — FAILS AA as text. Non-text only: dividers,
                         // icon strokes, chart gridlines. Muted copy uses ink2.
  green:     '#10B981',  // accent FILL only — 2.39:1, never use as text
  green2:    '#0EA372',  // accent fill, gradient end
  greenInk:  '#067A52',  // green TEXT + success/positive — 5.05:1
  greenSoft: 'rgba(16,185,129,0.10)',
  greenGlow: 'rgba(16,185,129,0.26)',
  gold:      '#C9A84C',  // Elite/premium ONLY — fills/marks, NOT text (2.15:1)
  gold2:     '#B8912E',  // darker FILL — gradient ends, badge backgrounds
  goldSoft:  '#F5EDD6',
  goldInk:   '#8C6D1E',  // gold TEXT only (4.57:1). Never use as a fill — it
                         // darkens gradients and costs the dark text on them
                         // contrast. Fills use gold / gold2.
  red:       '#E5484D',  // fill only (3.68:1). Error TEXT uses redInk.
  redInk:    '#C62828',  // error TEXT — 5.29:1
};

export const hairline = `0.5px solid ${DS.sep}`;
export const gsLine = `1px solid ${GS.line}`;
// Mirrors --shadow-card: a white card lifted off the canvas, in place of a
// hairline border. Inline-style twin of the `shadow-card` utility.
export const cardShadow = '0 1px 2px rgb(20 26 33 / 0.05), 0 4px 16px rgb(20 26 33 / 0.07)';

export const eyebrow: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: DS.textSec,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
};
